import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { skillColor } from "@/lib/skills";
import {
  Type,
  Zap,
  Repeat,
  BookOpen,
  RefreshCw,
  Sprout,
  BookText,
  Clock,
  CalendarDays,
} from "lucide-react";
import { t } from "@/i18n";

const CATS = [
  {
    titleKey: "practice.cat_reviser",
    items: [
      {
        tKey: "practice.item_rejouer_erreurs",
        to: "/my-errors",
        i: RefreshCw,
        skill: "exam",
        tagKey: "practice.tag_malin",
      },
      {
        tKey: "practice.item_revision_jour",
        to: "/review?scope=due",
        i: RefreshCw,
        skill: "exam",
      },
      {
        tKey: "practice.item_questions_cours",
        to: "/practice/course-questions",
        i: BookOpen,
        skill: "grammar",
        tagKey: "practice.tag_nouveau",
      },
    ],
  },
  {
    titleKey: "practice.cat_grammaire",
    items: [
      {
        tKey: "practice.item_conjugaison",
        to: "/practice/conjugation",
        i: Repeat,
        skill: "grammar",
        disabled: true,
      },
      {
        tKey: "practice.item_racines",
        to: "/practice/roots",
        i: Sprout,
        skill: "vocabulary",
        disabled: true,
      },
    ],
  },
  {
    titleKey: "practice.cat_lecture",
    items: [
      {
        tKey: "practice.item_lecture_graduee",
        to: "/practice/reading",
        i: BookOpen,
        skill: "reading",
        disabled: true,
      },
      {
        tKey: "practice.item_construction_phrases",
        to: "/practice/sentences",
        i: BookText,
        skill: "vocabulary",
        disabled: true,
      },
    ],
  },
];

const U_MIN = 4.2;
const U_MAX = 15;
const FILL = 0.98;
const PASSES = 7;

export default function Practice() {
  const nav = useNavigate();
  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const lastU = useRef(9);

  const fit = useCallback(() => {
    const card = cardRef.current;
    const inner = innerRef.current;
    if (!card || !inner) return;

    const avail = card.clientHeight;
    const width = card.clientWidth;
    if (!avail || !width) return;

    const uMax = Math.min(U_MAX, width / 30);
    const target = avail * FILL;

    let u = Math.min(Math.max(lastU.current, U_MIN), uMax);

    for (let pass = 0; pass < PASSES; pass++) {
      inner.style.setProperty("--u", `${u}px`);
      const measured = inner.scrollHeight;
      if (!measured) break;

      const ratio = target / measured;
      if (ratio > 0.99 && ratio < 1.01) break;

      const nextU = Math.min(Math.max(u * ratio, U_MIN), uMax);
      if (Math.abs(nextU - u) < 0.05) {
        u = nextU;
        break;
      }
      u = nextU;
    }

    inner.style.setProperty("--u", `${u}px`);
    lastU.current = u;
  }, []);

  useLayoutEffect(() => {
    fit();
  }, [fit]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(card);
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    if (document.fonts?.ready) document.fonts.ready.then(fit).catch(() => {});
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, [fit]);

  return (
    <>
      <style>{`
        .practice-container {
          position: fixed;
          top: var(--nav-top-mobile, 4rem);
          bottom: var(--nav-bottom-mobile, 4rem);
          left: 0;
          right: 0;
          z-index: 10;
          display: flex;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .practice-container {
            top: var(--nav-top-desktop, 5.5rem);
            bottom: 1.25rem;
            padding: 0 clamp(1rem, 2.5vw, 2rem);
            justify-content: center;
          }
        }

        .practice-card {
          width: 100%;
          height: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
          container-type: inline-size;
          container-name: pcard;
        }
        @media (min-width: 768px) {
          .practice-card { max-width: 1100px; }
        }

        .p-inner {
          --u: 9px;
          width: 100%;
          padding: 0 calc(var(--u) * 1.6);
        }

        .p-title {
          font-size: calc(var(--u) * 2.2);
          font-weight: 800;
          margin-bottom: calc(var(--u) * 0.3);
        }
        .p-subtitle {
          font-size: calc(var(--u) * 1.25);
          margin-bottom: calc(var(--u) * 1.2);
        }

        .p-hero-row {
          display: flex;
          flex-direction: row;
          gap: calc(var(--u) * 1);
          margin-bottom: calc(var(--u) * 0.8);
          align-items: stretch;
        }
        .p-hero-btn {
          position: relative;
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: calc(var(--u) * 0.45);
          border-radius: calc(var(--u) * 1.8);
          border-width: 2px;
          padding: calc(var(--u) * 2.2) calc(var(--u) * 1) calc(var(--u) * 1.1);
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .p-hero-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
        .p-hero-badge {
          position: absolute;
          top: calc(var(--u) * 0.85);
          left: 50%;
          transform: translateX(-50%);
          font-size: calc(var(--u) * 0.75);
          padding: calc(var(--u) * 0.22) calc(var(--u) * 0.65);
          border-radius: 999px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          white-space: nowrap;
        }
        .p-hero-icon {
          width: calc(var(--u) * 3.6);
          height: calc(var(--u) * 3.6);
          border-radius: calc(var(--u) * 1);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .p-hero-label {
          font-size: calc(var(--u) * 1.65);
          font-weight: 800;
        }

        .p-daily-plan {
          display: flex;
          flex-direction: column;
          gap: calc(var(--u) * 0.4);
          border-radius: calc(var(--u) * 1.5);
          padding: calc(var(--u) * 0.9) calc(var(--u) * 1.2);
          margin-bottom: calc(var(--u) * 1.6);
          background-color: rgba(245, 158, 11, 0.08);
          border: 1px dashed rgba(245, 158, 11, 0.4);
          cursor: not-allowed;
          filter: grayscale(0.5);
        }
        .p-daily-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .p-daily-title {
          font-size: calc(var(--u) * 1.4);
          font-weight: 800;
          color: #92400e;
          display: flex;
          align-items: center;
          gap: calc(var(--u) * 0.5);
        }
        .p-daily-desc {
          font-size: calc(var(--u) * 1.05);
          color: var(--muted-foreground);
          line-height: 1.3;
        }

        .p-cats { display: flex; flex-direction: column; gap: calc(var(--u) * 1.1); }
        .p-cat-head {
          display: flex;
          align-items: baseline;
          gap: calc(var(--u) * 0.6);
          margin-bottom: calc(var(--u) * 0.5);
        }
        .p-cat-title {
          font-size: calc(var(--u) * 1.5);
          font-weight: 700;
        }
        .p-cat-note {
          font-size: calc(var(--u) * 1);
          font-style: italic;
        }

        .p-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(calc(var(--u) * 14), 1fr));
          gap: calc(var(--u) * 0.6);
        }

        .p-item {
          position: relative;
          display: flex;
          flex-direction: column;
          border-radius: calc(var(--u) * 1.4);
          border-width: 1px;
          padding: calc(var(--u) * 0.7);
          text-align: left;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .p-item.active:hover {
          transform: translateY(-2px);
          box-shadow: 0 3px 10px rgba(0,0,0,0.06);
        }
        .p-item.disabled {
          cursor: not-allowed;
          filter: grayscale(1);
          opacity: 0.55;
        }

        .p-item-tag {
          position: absolute;
          top: calc(var(--u) * 0.5);
          right: calc(var(--u) * 0.5);
          font-size: calc(var(--u) * 0.72);
          padding: calc(var(--u) * 0.15) calc(var(--u) * 0.5);
          border-radius: 999px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: calc(var(--u) * 0.25);
        }

        .p-item-icon {
          width: calc(var(--u) * 2.8);
          height: calc(var(--u) * 2.8);
          border-radius: calc(var(--u) * 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: calc(var(--u) * 0.5);
          flex-shrink: 0;
        }
        .p-item-title {
          font-size: calc(var(--u) * 1.2);
          font-weight: 700;
          line-height: 1.2;
        }
      `}</style>

      <div className="practice-container bg-background md:bg-transparent">
        <div className="practice-card" ref={cardRef}>
          <div className="p-inner" ref={innerRef}>
            <h1 className="p-title font-head">
              {t("practice.centre_d_entrainement")}
            </h1>
            <p className="p-subtitle text-muted-foreground">
              {t("practice.entraine_toi_librement_sans_suivre_le")}
            </p>

            <div className="p-hero-row">
              <button
                onClick={() => nav("/practice/alphabet")}
                className="p-hero-btn border-amber-500 bg-amber-500/10 hover:bg-amber-500/20"
              >
                <span className="p-hero-badge bg-amber-500 text-white inline-block mb-3">
                  {t("practice.commencer_ici")}
                </span>
                <div className="p-hero-icon bg-amber-500 text-white shadow-sm mt-2">
                  <Type size={22} />
                </div>
                <div className="p-hero-label font-head">
                  {t("dashboard.apprendre_l_alphabet")}
                </div>
              </button>

              <button
                onClick={() => nav("/vocab-quiz")}
                className="p-hero-btn border-primary bg-primary/10 hover:bg-primary/20"
              >
                <span className="p-hero-badge invisible">
                  {t("practice.commencer_ici")}
                </span>
                <div className="p-hero-icon bg-primary text-primary-foreground shadow-sm">
                  <Zap size={22} />
                </div>
                <div className="p-hero-label font-head">
                  {t("dashboard.apprendre_des_mots")}
                </div>
              </button>
            </div>

            <div className="p-daily-plan">
              <div className="p-daily-header">
                <span className="p-daily-title font-head">
                  <CalendarDays size={18} /> {t("practice.daily_plan")}
                </span>
                <span
                  className="bg-muted text-muted-foreground"
                  style={{
                    fontSize: "calc(var(--u) * 0.72)",
                    padding: "calc(var(--u) * 0.15) calc(var(--u) * 0.5)",
                    borderRadius: "999px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    gap: "calc(var(--u) * 0.25)",
                  }}
                >
                  <Clock size={11} /> {t("practice.bientot")}
                </span>
              </div>
              <p className="p-daily-desc">
                {t("practice.enchainement_de_lecons_et_d_exercices")}
              </p>
            </div>

            <div className="p-cats">
              {CATS.map((c) => {
                const allDisabled = c.items.every((it) => it.disabled);
                return (
                  <div key={c.titleKey}>
                    <div className="p-cat-head">
                      <h2 className="p-cat-title font-head">{t(c.titleKey)}</h2>
                      {allDisabled && (
                        <span className="p-cat-note text-muted-foreground">
                          {t("practice.bientot_disponible")}
                        </span>
                      )}
                    </div>
                    <div className="p-grid">
                      {c.items.map((it, i) => {
                        const Comp = it.disabled ? "div" : "button";
                        const localizedTitle = t(it.tKey);
                        return (
                          <Comp
                            key={i}
                            data-testid={`practice-card-${localizedTitle}`}
                            onClick={it.disabled ? undefined : () => nav(it.to)}
                            aria-disabled={it.disabled || undefined}
                            tabIndex={it.disabled ? -1 : undefined}
                            className={`p-item bg-card ${
                              it.disabled
                                ? "disabled border"
                                : "active border hover:border-primary"
                            }`}
                          >
                            {it.disabled ? (
                              <span className="p-item-tag bg-muted text-muted-foreground">
                                <Clock size={11} />
                                {t("practice.bientot")}
                              </span>
                            ) : (
                              it.tagKey && (
                                <span className="p-item-tag bg-ocre/15 text-ocre">
                                  {t(it.tagKey)}
                                </span>
                              )
                            )}
                            <div
                              className="p-item-icon text-white shadow-sm"
                              style={{
                                background: it.disabled
                                  ? "var(--muted-foreground)"
                                  : skillColor(it.skill),
                              }}
                            >
                              <it.i size={16} />
                            </div>
                            <div className="p-item-title">{localizedTitle}</div>
                          </Comp>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
